import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import {
  setCustomerNrcStatus,
  setCustomerPage,
} from 'src/app/action/customer.actions';
import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { selectNrcStatus } from 'src/app/selector/customer.selectors';
import { selectPage } from 'src/app/selector/redeem.selectors';
import { CustomerApiService } from 'src/app/service/customer/customer-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

export interface CustomerListResponse {
  nextContinuation: number
  pageTotal: number
  allTotal: number
  customerInfoLst: CustomerInfoLst[]
}

export interface CustomerInfoLst {
  srNo: number
  loginPhone: string
  enabled: boolean
  name: string
  profile: string
  viberPhone?: string
  email: string
  occupation: string
  nrc: string
  companyRegisterNo: string
  address1: string
  address2: string
  address3: string
  city: string
  township: string
  divisionState: string
  currentPoints?: number
  currentPointsTs?: string
  totalPoints?: number
  dailyRedeemableLimit: number
  paymentProviderInfoRespLst: any[]
  nrcFrontImage: any
  nrcBackImage: any
  nrcVerifiedBy: any
  upgVerifiedBy: any
  hasIllegalClaims: boolean
  profilePhoto: any
  unverifiedReasonNote?: boolean
  customerType?: string
  infoRemark?: string
}

@Component({
  selector: 'app-customer-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.css'],
})
export class CustomerListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly apiService = inject(CustomerApiService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly store = inject(Store);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link layout dark theme visibility pointer instantly
  readonly isDarkMode = this.themeService.isDarkMode;

  // Fully refactored view state tracking using reactive signals
  readonly customerlist = signal<CustomerInfoLst[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // 1-based index template pagination matching optimization
  readonly pageSize = signal<number>(PageSize.CUSTOMER_LIST);

  // Processing indicators
  readonly loading = signal<boolean>(false);

  // Filtering Options Matrix Configuration
  readonly filterNRCStatus = [
    'ALL',
    'NRC_VERIFIED',
    'NRC_TO_VERIFY',
    'NRC_UNABLE_TO_VERIFY',
    'UNVERIFIED_TO_VERIFY',
  ];

  phone = '959';
  nrcStatus = 'ALL';
  isHeaderAccess = false;
  mathMin = Math.min;

  ngOnInit(): void {
    this.isHeaderAccess = this.checkAccessRoles.check([
      UserLoginRole.SUPER_ADMIN,
      UserLoginRole.ADMIN,
    ]);

    this.restorePreviousDataReloadWithAppState();
    this.getCustomerList();
  }

  private restorePreviousDataReloadWithAppState(): void {
    this.store.select(selectNrcStatus).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(nrc => this.nrcStatus = nrc || 'ALL');
    this.store.select(selectPage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(savedPage => {
      this.page.set((savedPage ?? 0) + 1); // Sync 0-based store states up to 1-based pagination view counters
    });
  }

  /**
   * Streams page-indexed user lists matching current criteria down from endpoints
   */
  getCustomerList(): void {
    this.loading.set(true);
    const zeroBasedPage = this.page() - 1; // Translate 1-based page signal back to 0-based parameters

    this.apiService.getCustomerList(this.nrcStatus, zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)) // Unconditionally drops active background loading wheels
      )
      .subscribe({
        next: (v: CustomerListResponse) => {
          this.customerlist.set(v?.customerInfoLst || []);
          this.total.set(v?.pageTotal || 0);
          this.allTotal.set(v?.allTotal || 0);
        },
        error: (err: any) => {
          console.error('Failed to download user ledger directories:', err);
          alert(err?.message || err || 'Network failure while processing data lists.');
        },
        complete: () => {
          this.saveCurrentStateToNgrxStore();
        },
      });
  }

  /**
   * Input keyword phone router lookup utility
   */
  SearchPhone(phoneKeyword: string): void {
    if (!phoneKeyword || !phoneKeyword.trim() || phoneKeyword === '959') {
      alert('Please enter a target account phone line keyword.');
      return;
    }
    this.router.navigate(['nav/customer-info/', phoneKeyword.trim()]);
  }

  /**
   * Router paths execution definitions using path parameter variables mapping to preserve routing rules
   */
  goToProfile(customerPhone: string): void {
    if (!customerPhone) return;
    this.router.navigate(['nav/customer-info/', customerPhone]);
  }

  goToQrHistory(customerPhone: string): void {
    if (!customerPhone) return;
    this.router.navigate(['nav/qr-code-history', customerPhone]);
  }

  goToCustomerRedeem(customerPhone: string): void {
    if (!customerPhone) return;
    this.router.navigate(['nav/customer-redeem-list', customerPhone]);
  }

  /**
   * Audits state flags before processing system restriction thresholds updates
   */
  UpdateRedeemLimitStatusForAllCustomer(): void {
    this.apiService.getStatusForUpdateRedeemLimit()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any) => {
          if (v?.Message?.includes('Kong sync in progress')) {
            alert('Daily Redeem limit synchronization task is already running...');
          } else {
            this.UpdateRedeemLimitForAllCustomer();
          }
        },
        error: (err: any) => {
          console.error('Failed to audit global redemption locking profile flags:', err);
          alert(err?.message || err || 'Network failure while verifying server operations.');
        }
      });
  }

  private UpdateRedeemLimitForAllCustomer(): void {
    const inputvalue = prompt('Enter new global daily redeemable limit amount:');
    if (inputvalue === null) return; // Graces out immediately if operator hits cancel on native popups

    if (!inputvalue.trim() || isNaN(Number(inputvalue))) {
      alert('A valid numeric integer constraint value must be provided.');
      return;
    }

    if (!confirm(`Confirm changes: Set global daily redemption ceiling restriction to ${inputvalue}?`)) {
      return;
    }

    this.apiService.getUpdateRedeemLimitForAllCustomer('ALL', String(inputvalue))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          alert('Success: All customers daily redeem limits are updates inside backend servers processing channels.');
        },
        error: (err: any) => {
          console.error('Failed to transmit cluster-wide account threshold mutations:', err);
          alert(err?.message || err || 'Failed to update system parameters constraints.');
        }
      });
  }

  goToCustomerUnVerifyList(): void {
    this.router.navigate(['/nav/customer-unverify-list']);
  }

  filterButton(): void {
    this.page.set(1); // Reset page parameters index back to page 1 upon condition filter revision
    this.getCustomerList();
  }

  /**
   * Keeps state cache configurations in perfect synchronization with your store layers
   */
  private saveCurrentStateToNgrxStore(): void {
    const zeroBasedPage = this.page() - 1;
    this.store.dispatch(setCustomerNrcStatus({ customerNrcStatus: this.nrcStatus }));
    this.store.dispatch(setCustomerPage({ customerPage: zeroBasedPage }));
  }

  /**
   * Captures event page shifts step sequences securely across viewport boundaries
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.getCustomerList();
    }
  }
}
